import { useCallback, useEffect, useRef, useState } from 'react';
const RECONNECT_INTERVAL = 3000;
export function useWebSocket(options = {}) {
    const { onMessage, reconnect = false, reconnectInterval = RECONNECT_INTERVAL } = options;
    const [isConnected, setIsConnected] = useState(false);
    const wsRef = useRef(null);
    const onMessageRef = useRef(onMessage);
    const reconnectTimeout = useRef(null);
    const disconnect = useCallback(() => {
        wsRef.current?.close();
        wsRef.current = null;
        setIsConnected(false);
    }, []);
    const connect = useCallback((url) => {
        return new Promise((resolve) => {
            if (wsRef.current) {
                resolve();
                return;
            }
            const ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onopen = () => {
                setIsConnected(true);
                reconnectTimeout.current && clearTimeout(reconnectTimeout.current);
                resolve();
            };
            ws.onmessage = (event) => {
                try {
                    onMessageRef.current?.(JSON.parse(event.data));
                }
                catch {
                    onMessageRef.current?.(event.data);
                }
            };
            ws.onerror = () => {
                ws.close();
            };
            ws.onclose = () => {
                setIsConnected(false);
                wsRef.current = null;
                if (reconnect) {
                    reconnectTimeout.current = setTimeout(() => connect(url), reconnectInterval);
                }
            };
        });
    }, [reconnect, reconnectInterval]);
    const sendMessage = useCallback((message) => {
        wsRef.current?.readyState === WebSocket.OPEN && wsRef.current.send(message);
    }, []);
    useEffect(function updateMessageRef() {
        onMessageRef.current = onMessage;
    }, [onMessage]);
    useEffect(function disconnectOnUnmount() {
        return disconnect;
    }, [disconnect]);
    return { isConnected, sendMessage, connect, disconnect };
}
